import React, { useEffect, useReducer, useState } from 'react';
import { FetchType } from '../../../enums';
import { IPeopleCMSLayoutProps } from './freemium-people.cms.models';
import { useAppActions } from '../../../overmind';
import * as store from './freemium-people.cms.store';
import { ManagePersonCascadingPanels } from './panels/manage-person-panels/freemium-manage-person-cascading-panels';
import { PeopleHeaderActionsCMSLayout } from './freemium-people-header-actions.cms.layout';
import { ITableDataRow, Modal, Panel, useMatchScreenWidth } from '@keplerco/core';
import { BaseSearchRequest } from '../../../models/overmind/search-request';
import { ColumnConfiguratorWidget } from '../../../widgets/layouts/column-configurator/column-configurator.widget';
import { FreemiumBulkUploadCascadingPanels } from './panels/bulk-upload-focus-panel/freemium-bulk-upload-cascading-panels';
import { FreemiumPeopleTableCMSLayout } from './freemium-people-table.cms.layout';
import { ConfirmationModalLayout } from '../../../widgets/layouts';

export function FreemiumPeopleCMSLayout(props: IPeopleCMSLayoutProps): JSX.Element {
  const actions = useAppActions();

  const isMobile = useMatchScreenWidth('mobile');

  const [state, dispatch] = useReducer(store.reducer, store.initialState);

  const [selectedRows, setSelectedRows] = useState<ITableDataRow[]>([]);
  const [loadingPeople, setLoadingPeople] = useState<string[]>([]);

  async function updateData(request: BaseSearchRequest) {
    // Fetch the data for the page from the api. If data
    // is not returned from the api, cancel the rest of
    // the fetch operation.
    const data = await actions.freemiumSearchPeople(request);
    if (!!props.onLoad) {
      props.onLoad();
    }
    // Populate the data into the page state so that a
    // re-render can be triggered in the view
    dispatch({ type: store.PeopleCMSActionTypes.SetData, payload: { data } });

    actions.stopLoader(props.path);
  }

  useEffect(() => {
    async function initPage() {
      actions.startLoader({ path: props.path, type: FetchType.PageFetching });

      dispatch({ type: store.PeopleCMSActionTypes.SetHeader, payload: props.slugs });

      // Constructs the request call and ensures that the
      // slugs for company, department and/or team are
      // populated in the request body
      const request: BaseSearchRequest = { ...state.request, ...props.slugs };
      dispatch({ type: store.PeopleCMSActionTypes.SetRequest, payload: request });

      await updateData(request);
    }

    initPage();
  }, []);

  useEffect(() => {
    if (!state.data) return;
    updateData(state.request);
  }, [state.request]);

  async function onClickSendActivationInvitationHandler(payload: { emails: string[]; shouldClearRows: boolean }) {
    if (!props.slugs.companySlug) return;

    actions.startLoader({ path: props.path, type: FetchType.Sending });

    actions.displayPaywall('send invites');

    if (payload.shouldClearRows) setSelectedRows([]);

    await updateData(state.request);
  }

  async function onClickSendActivationReminderHandler(payload: { emails: string[]; shouldClearRows: boolean }) {
    if (!props.slugs.companySlug) return;

    actions.startLoader({ path: props.path, type: FetchType.Sending });

    actions.displayPaywall('send invites');

    if (payload.shouldClearRows) setSelectedRows([]);

    await updateData(state.request);
  }

  function onClickCancelManagingColumns() {
    dispatch({ type: store.PeopleCMSActionTypes.ManageColumns, payload: { open: false, reset: false } });
  }

  function onCancelDeleteConfirmationModalHandler() {
    dispatch({ type: store.PeopleCMSActionTypes.SetPersonToDelete, payload: undefined });
  }

  return (
    <React.Fragment>
      <PeopleHeaderActionsCMSLayout
        path={props.path}
        isMobile={isMobile}
        request={state.request}
        setRequest={payload => dispatch({ type: store.PeopleCMSActionTypes.SetRequest, payload })}
        setPersonToManage={payload => dispatch({ type: store.PeopleCMSActionTypes.SetPersonToManage, payload })}
        setBulkUploadVisible={payload => dispatch({ type: store.PeopleCMSActionTypes.SetShowBulkUpload, payload })}
        selectedRows={selectedRows}
        setSelectedRows={async payload => {
          setSelectedRows(payload);
          await updateData(state.request);
          actions.stopLoader(props.path);
        }}
        data={state.data}
        columnConfiguration={state.columnConfiguration}
        onClickSendActivationInvitation={onClickSendActivationInvitationHandler}
        onClickSendActivationReminder={onClickSendActivationReminderHandler}
      />

      <div style={{ width: '100%' }}>
        <FreemiumPeopleTableCMSLayout
          path={props.path}
          request={state.request}
          setRequest={payload => dispatch({ type: store.PeopleCMSActionTypes.SetRequest, payload })}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          setManageColumns={payload => dispatch({ type: store.PeopleCMSActionTypes.ManageColumns, payload })}
          data={state.data}
          loadingPeople={loadingPeople}
          setLoadingPeople={async payload => {
            await updateData(state.request);
            setLoadingPeople(payload);
          }}
          setPersonToArchive={payload => dispatch({ type: store.PeopleCMSActionTypes.SetPersonToArchive, payload })}
          companySlug={props.slugs.companySlug}
          setPersonToManage={payload => dispatch({ type: store.PeopleCMSActionTypes.SetPersonToManage, payload })}
          columnConfiguration={state.columnConfiguration}
          onClickSendActivationInvitation={onClickSendActivationInvitationHandler}
          onClickSendActivationReminder={onClickSendActivationReminderHandler}
          setPersonToDelete={payload => dispatch({ type: store.PeopleCMSActionTypes.SetPersonToDelete, payload })}
        />
      </div>
      {!!state.personToManage?.show && (
        <ManagePersonCascadingPanels
          personId={state.personToManage.data}
          onCancel={() => {
            dispatch({ type: store.PeopleCMSActionTypes.SetPersonToManage, payload: { show: false, data: void 0 } });
            updateData(state.request);
          }}
        />
      )}

      {!!state.showBulkUpload?.show && (
        <FreemiumBulkUploadCascadingPanels
          onCancel={() => {
            dispatch({ type: store.PeopleCMSActionTypes.SetShowBulkUpload, payload: { show: false, data: undefined } });
            updateData(state.request);
          }}
        />
      )}

      <Panel open={!!state.managingColumns} onClose={onClickCancelManagingColumns}>
        <ColumnConfiguratorWidget
          columns={state.columnConfiguration}
          onReset={() => dispatch({ type: store.PeopleCMSActionTypes.ManageColumns, payload: { open: true, reset: true } })}
          onCancel={onClickCancelManagingColumns}
          onSave={payload => {
            dispatch({ type: store.PeopleCMSActionTypes.EditColumnConfiguration, payload });
            dispatch({ type: store.PeopleCMSActionTypes.ManageColumns, payload: { open: false } });
          }}
        />
      </Panel>

      <Modal open={!!state.personToDelete} onClose={onCancelDeleteConfirmationModalHandler}>
        {!!state.personToDelete && (
          <ConfirmationModalLayout
            title="Delete person"
            subtitle={`Are you sure you want to remove ${state.personToDelete.firstName} ${state.personToDelete.lastName} from your company? This will permanently delete all associated skill scores and data.`}
            subtitleSize="standard"
            submitButtonText="Yes, delete"
            onClickSubmit={async () => {
              if (!state.personToDelete?.id) return;

              const id = state.personToDelete?.id;
              dispatch({ type: store.PeopleCMSActionTypes.SetPersonToDelete, payload: undefined });

              setLoadingPeople(people => [...people, id]);
              await actions.freemiumDeletePerson(id);
              await updateData(state.request);
              setLoadingPeople(people => [...people.filter(pid => id !== pid)]);
            }}
            cancelButtonText="No, cancel"
            onClickCancel={onCancelDeleteConfirmationModalHandler}
          />
        )}
      </Modal>
    </React.Fragment>
  );
}
